import { toMinor } from '@ly360/core';
import { Dialog, ErrorState, Select, useFormat } from '@ly360/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Minus, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CRM_KEYS } from '../crm/data';
import { Field } from '../crm/forms';
import { PersonPicker } from '../crm/pickers';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { RETAIL_KEYS, shelfPrice, useProducts, useStores, type Product } from './data';
import { DialogButtons } from './ui';

export const ORDER_SOURCES = [
  'store',
  'online',
  'whatsapp',
  'instagram',
  'tiktok',
  'snapchat',
  'facebook',
  'webchat',
  'phone',
] as const;

/** A product as a dropdown option: brand, name and size, with price and stock. */
export function productOption(
  p: Product,
  t: (k: string, o?: Record<string, unknown>) => string,
  money: (m: number) => string,
) {
  return {
    value: p.id,
    label: `${p.brand} ${p.name}${p.sizeMl ? ` ${p.sizeMl} ml` : ''}`,
    hint: `${money(shelfPrice(p))} · ${p.stock === 0 ? t('retail.stock.out') : p.stock === null ? '' : t('retail.stock.left', { count: p.stock })}`,
    disabled: p.stock === 0,
  };
}

/**
 * Records a sale: in a store, online, or in a chat. From a conversation the customer, channel and conversation are
 * filled in, so the sale counts for that chat (and for the campaign or ad that started it).
 */
export function NewOrderDialog({
  onClose,
  personId: fixedPerson,
  source: fixedSource,
  conversationId,
  initialLines,
  onCreated,
}: {
  onClose: () => void;
  /** Lines to start from (a suggestion read from the chat). */
  initialLines?: { productId: string; quantity: number }[];
  personId?: string;
  source?: string;
  conversationId?: string;
  onCreated?: (id: string, number: string) => void;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const { session } = useAuth();
  const qc = useQueryClient();
  const products = useProducts();
  const stores = useStores();
  const currency = session!.tenant.currency;
  const money = (m: number) => fmt.money(m, currency);
  const [personId, setPersonId] = useState(fixedPerson ?? '');
  const [source, setSource] = useState(
    fixedSource && (ORDER_SOURCES as readonly string[]).includes(fixedSource) ? fixedSource : 'store',
  );
  const [storeId, setStoreId] = useState('');
  const [lines, setLines] = useState<{ productId: string; quantity: number }[]>(initialLines ?? []);
  const [adding, setAdding] = useState('');
  const [coupon, setCoupon] = useState('');
  const [discount, setDiscount] = useState('');
  const byId = new Map((products.data?.items ?? []).map((p) => [p.id, p]));
  const subtotal = lines.reduce((s, l) => s + shelfPrice(byId.get(l.productId)!) * l.quantity, 0);
  const discountMinor = toMinor(Number(discount || 0), currency);
  const m = useMutation({
    mutationFn: () =>
      api<{ id: string; number: string }>('/v1/orders', {
        method: 'POST',
        body: JSON.stringify({
          personId: personId || null,
          source,
          storeId:
            source === 'store'
              ? storeId || null
              : source === 'online'
                ? (stores.data?.items.find((s) => s.kind === 'online')?.id ?? null)
                : null,
          items: lines,
          couponCode: coupon.trim() || null,
          discount: discount ? Number(discount) : undefined,
          conversationId: conversationId ?? null,
        }),
      }),
    onSuccess: (r) => {
      for (const k of [...RETAIL_KEYS, ...CRM_KEYS]) void qc.invalidateQueries({ queryKey: [k] });
      onCreated?.(r.id, r.number);
      onClose();
    },
  });
  const add = (id: string) => {
    if (!id) return;
    setLines((ls) =>
      ls.some((l) => l.productId === id)
        ? ls.map((l) => (l.productId === id ? { ...l, quantity: l.quantity + 1 } : l))
        : [...ls, { productId: id, quantity: 1 }],
    );
    setAdding('');
  };
  const physical = (stores.data?.items ?? []).filter((s) => s.kind !== 'online');

  return (
    <Dialog
      open
      wide
      title={t('retail.order.new')}
      onClose={onClose}
      onSubmit={() => m.mutate()}
      testId="new-order"
      footer={
        <DialogButtons
          onClose={onClose}
          busy={m.isPending}
          disabled={!lines.length || (source === 'store' && !storeId)}
          label={t('retail.order.record', { total: money(Math.max(0, subtotal - discountMinor)) })}
        />
      }
    >
      <div className="form-grid">
        {!fixedPerson && (
          <Field id="no-person" label={t('retail.order.customer')}>
            <PersonPicker id="no-person" value={personId} onChange={setPersonId} />
          </Field>
        )}
        <Field id="no-source" label={t('retail.order.source')}>
          <Select
            id="no-source"
            value={source}
            onChange={setSource}
            options={ORDER_SOURCES.map((s) => ({ value: s, label: t(`retail.source.${s}`) }))}
          />
        </Field>
        {source === 'store' && (
          <Field id="no-store" label={t('retail.order.store')}>
            <Select
              id="no-store"
              value={storeId}
              onChange={setStoreId}
              placeholder={t('retail.order.chooseStore')}
              options={physical.map((s) => ({ value: s.id, label: s.name, hint: s.nameAr ?? undefined }))}
            />
          </Field>
        )}
      </div>
      <Field id="no-add" label={t('retail.order.addProduct')}>
        <Select
          id="no-add"
          value={adding}
          onChange={add}
          placeholder={t('retail.order.searchProduct')}
          data-testid="order-product"
          options={(products.data?.items ?? []).map((p) => productOption(p, t, money))}
        />
      </Field>
      {lines.length > 0 && (
        <table className="table order-lines" data-testid="order-lines">
          <tbody>
            {lines.map((l) => {
              const p = byId.get(l.productId)!;
              return (
                <tr key={l.productId}>
                  <td dir="auto">
                    <strong>{p.brand}</strong> {p.name}
                    {p.sizeMl && <span className="faint"> · {p.sizeMl} ml</span>}
                  </td>
                  <td className="qty">
                    <button
                      type="button"
                      className="icon-btn"
                      aria-label={t('retail.order.less')}
                      onClick={() =>
                        setLines((ls) =>
                          ls.map((x) =>
                            x.productId === l.productId ? { ...x, quantity: Math.max(1, x.quantity - 1) } : x,
                          ),
                        )
                      }
                    >
                      <Minus size={14} />
                    </button>
                    <span className="num">{l.quantity}</span>
                    <button
                      type="button"
                      className="icon-btn"
                      aria-label={t('retail.order.more')}
                      onClick={() =>
                        setLines((ls) =>
                          ls.map((x) =>
                            x.productId === l.productId ? { ...x, quantity: x.quantity + 1 } : x,
                          ),
                        )
                      }
                    >
                      <Plus size={14} />
                    </button>
                  </td>
                  <td className="num">{money(shelfPrice(p) * l.quantity)}</td>
                  <td>
                    <button
                      type="button"
                      className="icon-btn"
                      aria-label={t('common.remove')}
                      onClick={() => setLines((ls) => ls.filter((x) => x.productId !== l.productId))}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <div className="form-grid">
        <Field id="no-coupon" label={t('retail.order.coupon')} hint={t('retail.order.couponHint')}>
          <input
            id="no-coupon"
            className="input mono"
            dir="ltr"
            value={coupon}
            onChange={(e) => setCoupon(e.target.value.toUpperCase())}
          />
        </Field>
        <Field id="no-discount" label={t('retail.order.discount')}>
          <input
            id="no-discount"
            className="input"
            type="number"
            min={0}
            step="any"
            inputMode="decimal"
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
          />
        </Field>
      </div>
      {m.isError && <ErrorState error={m.error} />}
    </Dialog>
  );
}
